/**
 * Générateur — Produire des formules (double, triple, carré, successeur,
 * prédécesseur, aire, périmètre, etc.).
 * Attendu ID : 932 | Niveau : 5ème | Domaine : Nombres et calculs / Calcul littéral
 *
 * Plan de cartes (mise en conformité avec la spécification 932, campagne 5e,
 * 2026-09-22) :
 *
 *   Carte « Exprimer en fonction de la lettre » (id voc) — niveaux 1 et 2
 *     voc_texte_trou_drag : quatre types à une variable, étiquettes à glisser.
 *     voc_qcm_unique : un type, propositions adossées aux confusions nommées.
 *     voc_texte : saisie libre de la formule, toute écriture équivalente admise.
 *     voc_qcm_multiple : le travail sur l'ÉCRITURE — plusieurs propositions
 *       sont justes (forme simplifiée et non simplifiée), la consigne demande
 *       de toutes les cocher, et « x2 » est nommée comme illisible.
 *
 *   Carte « Relier phrases et expressions littérales » (id relier) — niveau 3
 *     relier_relier : quatre phrases à relier, toujours une famille
 *       parenthésée et une famille kx ± a (la place des parenthèses).
 *
 *   Carte « Deux nombres, une formule » (id deux) — niveau 3
 *     deux_texte : somme, différence, produit ou quotient de deux nombres,
 *       deux lettres distinctes.
 *
 *   Carte « Écrire tous les nombres… » (id general) — niveau 3
 *     general_qcm_unique : nombre pair, nombre impair, multiple d'un entier —
 *       les propositions fausses sont des réponses justes pour UNE valeur.
 *
 *   Carte « Traduire un programme de calcul » (id programme) — niveau 4
 *     programme_texte : deux instructions, forme réduite ax + b attendue.
 *
 *   Carte « Aire et périmètre d'un rectangle » (id aire) — niveau 5
 *     aire_texte_trou_saisie : tableau de valeurs puis coefficient de la formule.
 *     aire_qcm_unique : la formule de l'aire, avec le périmètre en distracteur.
 *
 *   Carte « Périmètre du polygone en fonction de x » (id perimetre) — niveau 5
 *     perimetre_qcm_unique + perimetre_texte_trou_saisie : quadrilatère donné,
 *       deux longueurs chiffrées, une longueur inconnue.
 *
 *   Carte « Périmètre d'une figure composée » (id composee) — niveau 7
 *     composee_qcm_unique : rectangle et carré accolés, DEUX longueurs
 *       inconnues, l'aire proposée en distracteur.
 *
 *   Carte « Suite de motifs : trouver la formule » (id motifs) — niveau 6
 *     motifs_texte_trou_saisie + motifs_qcm_unique : trois motifs si la suite
 *       est linéaire, quatre si elle est affine ; rang demandé entre 5 et 100.
 *
 * Spécification (C:\tanoed\Attendu-5e\specifications-tanoed\5e\932.json) :
 *   Bornes appliquées : les 18 types de formule de la liste fermée ; lettres
 *   tirées dans x, y, z, t, a, b, c, n, m (jamais e, i ni o) ; une ou deux
 *   variables ; multiplicateur 2..10 ; coefficients de programme de calcul
 *   2..11 ; longueur chiffrée sur une figure 2..12 ; 3 motifs montrés si la
 *   suite est linéaire, 4 si elle est affine ; rang demandé 5..100 ; une à
 *   quatre figures assemblées ; π et conversions d'unités NON activés.
 *   Exclusions appliquées : aucune identité remarquable ni développement à
 *   faire ; aucune suite de motifs à quotient (formules kn + c entières) ;
 *   aucun π, aucun arc ; aucune conversion ; jamais e, i ni o ; deux lettres
 *   toujours distinctes ; aucune figure à construire ni à mesurer ; rang
 *   demandé toujours supérieur au nombre de motifs montrés.
 *   Erreurs visées → mots-clés :
 *     double confondu avec le carré        → double_et_carre
 *     triple confondu avec le cube         → triple_et_cube
 *     successeur et prédécesseur échangés  → successeur_et_predecesseur
 *     inverse confondu avec l'opposé       → inverse_et_oppose
 *     moitié lue comme « moins 0,5 »       → moitie_et_moins_zero_cinq
 *     opération traduite par une autre     → operation_traduite_a_tort
 *     coefficient écrit après la lettre    → coefficient_apres_la_lettre
 *     réponse juste pour une seule valeur  → juste_pour_une_valeur
 *     proportionnalité supposée (motif affine) → proportionnalite_supposee
 *     rayon confondu avec le diamètre      → rayon_et_diametre (NON SERVI :
 *       le réglage « cercles et arcs » est désactivé par défaut)
 *     aire donnée pour un périmètre        → aire_et_perimetre
 *   Graduation → cartes : 1 → voc_qcm_unique, voc_texte_trou_drag ;
 *   2 → voc_texte ; 3 → deux_texte, general_qcm_unique, relier_relier ;
 *   4 → programme_texte ; 5 → aire_*, perimetre_* ; 6 → motifs_* ;
 *   7 → composee_qcm_unique ; réglages transverses (π, unités) → aucune carte.
 *   Non tranché : le contrôle d'une saisie littérale se fait sur une LISTE
 *   finie d'écritures admises (il n'existe pas de moteur d'équivalence dans le
 *   dépôt), là où `points_de_vigilance` 1 demande l'équivalence algébrique ;
 *   la liste est auditable et la consigne ne demande pas de forme particulière,
 *   sauf pour le programme de calcul où la forme réduite est annoncée.
 *   Le format « relier » n'est pas l'un des trois formats du `type_reponse` :
 *   carte gardée, à confirmer.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, makeRelier, filtrerDistracteurs,
    ecrituresPolynome, tex,
} from '../../../utils';

const ATTENDU_ID = 932;

const L_VOC = 'Exprimer en fonction de n (double, moitié…)';
const L_RELIER = 'Relier phrases et expressions littérales';
const L_DEUX = 'Deux nombres, une formule';
const L_GENERAL = 'Écrire tous les nombres pairs, impairs, multiples';
const L_PROGRAMME = 'Traduire un programme de calcul';
const L_AIRE = 'Aire et périmètre d\'un rectangle';
const L_PERIMETRE = 'Périmètre du polygone en fonction de x';
const L_COMPOSEE = 'Périmètre d\'une figure composée';
const L_MOTIFS = 'Suite de motifs : trouver la formule';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOINS = '−';   // U+2212 (affichage et étiquettes drag ; jamais en saisie)

/**
 * Une lettre de variable s'écrit en italique mathématique, jamais en lettre ordinaire
 * (règle d'équipe, retour prof 2026-09-24). Deux rendus, deux écritures :
 *   - dans un ÉNONCÉ (payload.texte), la regex de rendu exige une commande LaTeX en tête :
 *     tex() pose le \\mathrm{} de largeur nulle qui la satisfait ;
 *   - dans une PHRASE À TROUS mixte, \\mathrm{} est interdit : le « $x$ » nu suffit, la regex
 *     y est permissive.
 */
const lettreEnonce = (L) => tex(L);
const lettreTrou = (L) => `$${L}$`;

/**
 * La même règle pour une EXPRESSION entière (un choix de QCM, une étiquette de
 * relier, une expression citée dans un énoncé) : elle passe en KaTeX d'un bloc,
 * la lettre sort en italique et les nombres restent droits.
 *
 * `enLatex` traduit notre écriture d'AFFICHAGE en LaTeX ; sans elle KaTeX rendrait
 * un nœud `.katex-error` sur « x² » et espacerait la virgule de « 3,5 ».
 *
 * ⚠️ Dans une PHRASE À TROUS, ne jamais produire une ligne qui commence ET finit
 * par « $ » : `hasMathLines` ferait basculer toute la phrase en mode KaTeX global.
 */
const enLatex = (s) => String(s)
    .replace(/²/g, '^2')
    .replace(/³/g, '^3')
    .replace(/−/g, '-')
    .replace(/\s*×\s*/g, ' \\times ')
    .replace(/\s*÷\s*/g, ' \\div ')
    .replace(/(\d),(\d)/g, '$1{,}$2');
/** Déjà en `$…$` (sortie de `frL`) → laissée telle quelle. */
const mathEnonce = (s) => (String(s).includes('$') ? String(s) : tex(enLatex(s)));
const mathChoix = (s) => (String(s).includes('$') ? String(s) : `$${enLatex(s)}$`);

// ─── Bornes de la spécification ─────────────────────────────────────────
// variables.lettre_de_la_variable : liste fermée de 9 lettres ; e, i et o
// exclues (exclusions 5).
const LETTRES = ['x', 'y', 'z', 't', 'a', 'b', 'c', 'n', 'm'];
const MULT_MIN = 2;
const MULT_MAX = 10;
const COEF_PROG_MIN = 2;
const COEF_PROG_MAX = 11;
const LONGUEUR_MIN = 2;
const LONGUEUR_MAX = 12;
const RANG_MIN = 5;
const RANG_MAX = 100;

const deuxLettres = () => {
    const [a, b] = shuffle(LETTRES).slice(0, 2);   // exclusions 6 : toujours distinctes
    return [a, b];
};

// « x/2 » en fraction verticale KaTeX là où l'élève la lit : énoncé et choix
// du QCM (audit fractions en texte, 2026-09-06). Les étiquettes à glisser
// gardent le texte : une tuile ne peut pas contenir d'accolades.
const frL = (s) => String(s).replace(/^([a-z0-9]+)\/([a-z0-9]+)$/, (m, a, b) => `$\\dfrac{${a}}{${b}}$`);

// ═════════ Types de formule (variables.type_de_formule : liste fermée de 18) ═════════

/**
 * Chaque type porte : son nom affiché, l'expression attendue, les écritures
 * admises en saisie, et ses propositions fausses nommées.
 * `vars` = 1 ou 2 (variables.nombre_de_variables).
 */
/**
 * Les écritures justes d'une fraction de la lettre : la barre de fraction, la multiplication par
 * la fraction dans les deux ordres, et la forme décimale quand elle existe. Le scoring retire les
 * espaces et remplace la virgule par un point (normaliserExpression), inutile donc de lister les
 * variantes d'espacement ni le point décimal.
 *
 * Motif : « a*1/4 » pour le quart de a est mathématiquement juste et était compté faux, alors que
 * la consigne promettait d'accepter toutes les écritures équivalentes (retour prof 2026-09-24).
 */
const ecrituresFraction = (L, den, dec) => {
    const out = [`${L}/${den}`, `${L}*1/${den}`, `1/${den}*${L}`];
    if (dec) out.push(`${dec}${L}`, `${dec}*${L}`, `${L}*${dec}`);
    return out;
};

const TYPES_FORMULE = [
    {
        id: 'double', vars: 1, niveau: 1, nom: 'le double de',
        expr: (L) => `2${L}`,
        ecritures: (L) => [`2${L}`, `2*${L}`, `${L}*2`, `${L}+${L}`],
        pieges: (L) => [
            { valeur: `${L}²`, code: 'double_et_carre' },
            { valeur: `${L} + 2`, code: 'operation_traduite_a_tort' },
            { valeur: `${L}/2`, code: 'moitie_et_moins_zero_cinq' },
            { valeur: `${L}2`, code: 'coefficient_apres_la_lettre' },
        ],
    },
    {
        id: 'triple', vars: 1, niveau: 1, nom: 'le triple de',
        expr: (L) => `3${L}`,
        ecritures: (L) => [`3${L}`, `3*${L}`, `${L}*3`, `${L}+${L}+${L}`],
        pieges: (L) => [
            { valeur: `${L}³`, code: 'triple_et_cube' },
            { valeur: `${L} + 3`, code: 'operation_traduite_a_tort' },
            { valeur: `${L}/3`, code: 'operation_traduite_a_tort' },
            { valeur: `${L}3`, code: 'coefficient_apres_la_lettre' },
        ],
    },
    {
        id: 'moitie', vars: 1, niveau: 1, nom: 'la moitié de',
        expr: (L) => `${L}/2`,
        ecritures: (L) => ecrituresFraction(L, 2, '0,5'),
        pieges: (L) => [
            { valeur: `${L} ${MOINS} 0,5`, code: 'moitie_et_moins_zero_cinq' },
            { valeur: `2${L}`, code: 'double_et_carre' },
            { valeur: `${L} ${MOINS} 2`, code: 'operation_traduite_a_tort' },
        ],
    },
    {
        id: 'quart', vars: 1, niveau: 1, nom: 'le quart de',
        expr: (L) => `${L}/4`,
        ecritures: (L) => ecrituresFraction(L, 4, '0,25'),
        pieges: (L) => [
            { valeur: `${L} ${MOINS} 4`, code: 'operation_traduite_a_tort' },
            { valeur: `4${L}`, code: 'double_et_carre' },
            { valeur: `${L}/2`, code: 'moitie_et_moins_zero_cinq' },
        ],
    },
    {
        id: 'dixieme', vars: 1, niveau: 1, nom: 'le dixième de',
        expr: (L) => `${L}/10`,
        ecritures: (L) => ecrituresFraction(L, 10, '0,1'),
        pieges: (L) => [
            { valeur: `${L} ${MOINS} 10`, code: 'operation_traduite_a_tort' },
            { valeur: `10${L}`, code: 'double_et_carre' },
            { valeur: `${L}/2`, code: 'moitie_et_moins_zero_cinq' },
        ],
    },
    {
        id: 'successeur', vars: 1, niveau: 1, nom: 'le nombre entier qui suit',
        expr: (L) => `${L} + 1`,
        ecritures: (L) => [`${L}+1`, `1+${L}`],
        pieges: (L) => [
            { valeur: `${L} ${MOINS} 1`, code: 'successeur_et_predecesseur' },
            { valeur: `2${L}`, code: 'double_et_carre' },
            { valeur: `${L} + 2`, code: 'operation_traduite_a_tort' },
        ],
    },
    {
        id: 'predecesseur', vars: 1, niveau: 1, nom: 'le nombre entier qui précède',
        expr: (L) => `${L} ${MOINS} 1`,
        ecritures: (L) => [`${L}-1`, `${L}+(-1)`],
        pieges: (L) => [
            { valeur: `${L} + 1`, code: 'successeur_et_predecesseur' },
            { valeur: `${L}/2`, code: 'moitie_et_moins_zero_cinq' },
            { valeur: `${L} ${MOINS} 2`, code: 'operation_traduite_a_tort' },
        ],
    },
    {
        id: 'carre', vars: 1, niveau: 1, nom: 'le carré de',
        expr: (L) => `${L}²`,
        ecritures: (L) => [`${L}²`, `${L}*${L}`],
        pieges: (L) => [
            { valeur: `2${L}`, code: 'double_et_carre' },
            { valeur: `${L} + 2`, code: 'operation_traduite_a_tort' },
            { valeur: `${L}³`, code: 'triple_et_cube' },
        ],
    },
    {
        id: 'cube', vars: 1, niveau: 1, nom: 'le cube de',
        expr: (L) => `${L}³`,
        ecritures: (L) => [`${L}^3`, `${L}³`, `${L}*${L}*${L}`],
        pieges: (L) => [
            { valeur: `3${L}`, code: 'triple_et_cube' },
            { valeur: `${L}²`, code: 'double_et_carre' },
            { valeur: `${L} + 3`, code: 'operation_traduite_a_tort' },
        ],
    },
    {
        id: 'oppose', vars: 1, niveau: 1, nom: 'l\'opposé de',
        expr: (L) => `${MOINS}${L}`,
        ecritures: (L) => [`-${L}`, `0-${L}`, `-1*${L}`, `(-1)*${L}`],
        pieges: (L) => [
            { valeur: `1/${L}`, code: 'inverse_et_oppose' },
            { valeur: `${L} ${MOINS} 1`, code: 'successeur_et_predecesseur' },
            { valeur: `${L}²`, code: 'double_et_carre' },
        ],
    },
    {
        id: 'inverse', vars: 1, niveau: 1, nom: 'l\'inverse de',
        expr: (L) => `1/${L}`,
        ecritures: (L) => [`1/${L}`, `1÷${L}`],
        pieges: (L) => [
            { valeur: `${MOINS}${L}`, code: 'inverse_et_oppose' },
            { valeur: `${L}/2`, code: 'moitie_et_moins_zero_cinq' },
            { valeur: `${L} ${MOINS} 1`, code: 'successeur_et_predecesseur' },
        ],
    },
];

// Types à DEUX variables (variables.nombre_de_variables max 2), niveau 3.
const TYPES_DEUX = [
    {
        id: 'somme', nom: (A, B) => `la somme de ${A} et de ${B}`,
        expr: (A, B) => `${A} + ${B}`,
        ecritures: (A, B) => [`${A}+${B}`, `${B}+${A}`],
        pieges: (A, B) => [
            { valeur: `${A}${B}`, code: 'operation_traduite_a_tort' },
            { valeur: `${A} ${MOINS} ${B}`, code: 'operation_traduite_a_tort' },
            { valeur: `${A}/${B}`, code: 'operation_traduite_a_tort' },
        ],
    },
    {
        id: 'difference', nom: (A, B) => `la différence entre ${A} et ${B}`,
        expr: (A, B) => `${A} ${MOINS} ${B}`,
        ecritures: (A, B) => [`${A}-${B}`],
        pieges: (A, B) => [
            { valeur: `${A}/${B}`, code: 'operation_traduite_a_tort' },
            { valeur: `${A} + ${B}`, code: 'operation_traduite_a_tort' },
            { valeur: `${B} ${MOINS} ${A}`, code: 'operation_traduite_a_tort' },
        ],
    },
    {
        id: 'produit', nom: (A, B) => `le produit de ${A} par ${B}`,
        expr: (A, B) => `${A}${B}`,
        ecritures: (A, B) => [`${A}${B}`, `${B}${A}`, `${A}*${B}`, `${B}*${A}`],
        pieges: (A, B) => [
            { valeur: `${A} + ${B}`, code: 'operation_traduite_a_tort' },
            { valeur: `${A} ${MOINS} ${B}`, code: 'operation_traduite_a_tort' },
            { valeur: `${A}/${B}`, code: 'operation_traduite_a_tort' },
        ],
    },
    {
        id: 'quotient', nom: (A, B) => `le quotient de ${A} par ${B}`,
        expr: (A, B) => `${A}/${B}`,
        ecritures: (A, B) => [`${A}/${B}`],
        pieges: (A, B) => [
            { valeur: `${A} ${MOINS} ${B}`, code: 'operation_traduite_a_tort' },
            { valeur: `${B}/${A}`, code: 'operation_traduite_a_tort' },
            { valeur: `${A}${B}`, code: 'operation_traduite_a_tort' },
        ],
    },
];

/**
 * Mémoire courte : le même type de formule ne sort pas deux fois de suite quand le professeur
 * enchaîne les questions d une meme carte. Le tirage est déjà équitable (11 types, une répétition
 * immédiate tous les 100 tirages environ), mais deux « l opposé de a » à la suite se voient et
 * donnent l impression d un générateur en panne (retour prof 2026-09-24). Même motif que gen_1114.
 */
const sansRepeter = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.type === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.type;
    return payload;
};
const refVocQcm = { v: null };
const refVocSaisie = { v: null };

// ═════════ Carte 1 — Exprimer en fonction de la lettre (niveaux 1 et 2) ═════════

const majuscule = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const genVocDrag = () => {
    const L = randomChoice(LETTRES);
    const types = shuffle(TYPES_FORMULE).slice(0, 4);
    // Une expression par ligne (convention 2026-09-02 : jamais « ; » entre
    // deux trous indépendants), chaque ligne commençant par une majuscule.
    // La lettre est en italique dans la phrase ; les ÉTIQUETTES, elles, gardent le
    // texte brut : un trou ne peut contenir ni « $ » ni accolade, et un leurre écrit
    // autrement que les bonnes réponses se repérerait sans lire l'exercice.
    const phrase = types.map(t => majuscule(`${t.nom} ${lettreTrou(L)} : {{${t.expr(L)}}}`)).join('\n');
    const horsTirage = TYPES_FORMULE.filter(t => !types.includes(t)).map(t => t.expr(L));
    const candidats = shuffle([
        ...horsTirage,
        `${L} + 2`, `${L} ${MOINS} 2`, `${L} + 3`, `${L} ${MOINS} 3`,
    ]);
    const distracteurs = filtrerDistracteurs(phrase, candidats).slice(0, 4);
    const payload = makeTexteTrou(phrase,
        // La consigne dit le geste réel : l'élève n'écrit aucune expression, il associe une
        // étiquette toute faite à chaque description (retour prof 2026-09-24).
        `<p><b>${lettreEnonce(L)} est un nombre entier.</b> Pour chaque ligne, associe l'expression qui convient, `
        + `en fonction de ${lettreEnonce(L)}.</p>`,
        { ...opts(L_VOC), mode: 'drag', distracteurs });
    payload._meta.params = {
        L, types: types.map(t => t.id), niveau: 1, nbVariables: 1,
        erreurVisee: ['double_et_carre', 'triple_et_cube', 'successeur_et_predecesseur'],
    };
    return payload;
};

const construireVocQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const L = randomChoice(LETTRES);
        const t = randomChoice(TYPES_FORMULE);
        const direction = randomChoice(['versExpression', 'versPhrase']);
        if (direction === 'versExpression') {
            const correct = mathChoix(frL(t.expr(L)));
            const pieges = shuffle(t.pieges(L)).slice(0, 3);
            const choix = [correct, ...pieges.map(p => mathChoix(frL(p.valeur)))];
            if (new Set(choix).size !== choix.length) continue;
            const payload = makeQcm(choix, correct,
                `<p><b>${lettreEnonce(L)} est un nombre entier. Quelle expression représente `
                + `« ${t.nom} ${lettreEnonce(L)} » ?</b></p>`, opts(L_VOC));
            payload._meta.params = {
                L, type: t.id, direction, niveau: 1, nbVariables: 1,
                distracteurs: pieges.map(p => ({ code: p.code, valeur: mathChoix(frL(p.valeur)) })),
            };
            return payload;
        }
        const correct = `${t.nom} ${lettreTrou(L)}`;
        const autres = shuffle(TYPES_FORMULE.filter(u => u !== t)).slice(0, 3);
        const choix = [correct, ...autres.map(u => `${u.nom} ${lettreTrou(L)}`)];
        if (new Set(choix).size !== choix.length) continue;
        const payload = makeQcm(choix, correct,
            `<p><b>${lettreEnonce(L)} est un nombre entier. Que représente l'expression `
            + `${mathEnonce(frL(t.expr(L)))} ?</b></p>`, opts(L_VOC));
        payload._meta.params = {
            L, type: t.id, direction, niveau: 1, nbVariables: 1,
            distracteurs: autres.map(u => ({
                code: u.id === 'carre' || u.id === 'double' ? 'double_et_carre'
                    : u.id === 'cube' || u.id === 'triple' ? 'triple_et_cube'
                        : u.id === 'successeur' || u.id === 'predecesseur' ? 'successeur_et_predecesseur'
                            : u.id === 'inverse' || u.id === 'oppose' ? 'inverse_et_oppose'
                                : 'operation_traduite_a_tort',
                valeur: `${u.nom} ${lettreTrou(L)}`,
            })),
        };
        return payload;
    }
    // Repli déterministe : le double de x
    const t = TYPES_FORMULE[0];
    const pieges = t.pieges('x').slice(0, 3);
    const payload = makeQcm([mathChoix('2x'), ...pieges.map(p => mathChoix(frL(p.valeur)))], mathChoix('2x'),
        `<p><b>${lettreEnonce('x')} est un nombre entier. Quelle expression représente `
        + `« le double de ${lettreEnonce('x')} » ?</b></p>`,
        opts(L_VOC));
    payload._meta.params = {
        L: 'x', type: 'double', direction: 'versExpression', niveau: 1, nbVariables: 1,
        distracteurs: pieges.map(p => ({ code: p.code, valeur: mathChoix(frL(p.valeur)) })),
    };
    return payload;
};

const construireVocSaisie = () => {
    const L = randomChoice(LETTRES);
    const t = randomChoice(TYPES_FORMULE);
    const ecritures = t.ecritures(L);
    const payload = makeSaisie(ecritures[0],
        `<p><b>${lettreEnonce(L)} est un nombre entier. Écris ${t.nom} ${lettreEnonce(L)} `
        + `en fonction de ${lettreEnonce(L)}.</b> `
        + 'Plusieurs écritures sont acceptées, par exemple avec une fraction ou un nombre '
        + 'décimal ; '
        + 'le coefficient s\'écrit devant la lettre, jamais après.</p>',
        { ...opts(L_VOC), comparaison: 'expression', reponses_acceptees: ecritures });
    payload._meta.params = {
        L, type: t.id, niveau: 2, nbVariables: 1, ecritures,
        erreurVisee: ['coefficient_apres_la_lettre'],
    };
    return payload;
};

/**
 * Le travail sur l'ÉCRITURE (type_reponse) : plusieurs propositions sont
 * justes, la consigne demande de toutes les cocher, et chaque proposition
 * fausse porte la confusion qu'elle nomme.
 */
// Seuls le double et le triple offrent TROIS écritures également lisibles :
// le nombre de propositions reste constant (trois justes, deux fausses).
const TYPES_ECRITURE = TYPES_FORMULE.filter(t => ['double', 'triple'].includes(t.id));

const genVocQcmMultiple = () => {
    for (let essai = 0; essai < 40; essai++) {
        const L = randomChoice(LETTRES);
        const t = randomChoice(TYPES_ECRITURE);
        const justes = t.ecritures(L)
            .map(e => e.replace(/\*/g, ' × '))
            .slice(0, 3)
            .map(mathChoix);
        const pieges = shuffle(t.pieges(L)).slice(0, 2)
            .map(p => ({ code: p.code, valeur: mathChoix(p.valeur) }));
        const choix = [...justes, ...pieges.map(p => p.valeur)];
        if (new Set(choix).size !== choix.length) continue;
        const payload = makeQcm(choix, justes,
            // La spec exige qu'une écriture refusée soit nommée dans la consigne : « l'écriture
            // d'un coefficient après la lettre est la seule que nous refusons sans discussion, et
            // l'élève doit savoir pourquoi ». Elle ne l'était pas ici (retour prof 2026-09-24).
            `<p><b>${lettreEnonce(L)} est un nombre entier. Coche TOUTES les écritures correctes de `
            + `« ${t.nom} ${lettreEnonce(L)} ».</b> Une même formule s'écrit de plusieurs façons. `
            + 'Attention : le nombre s\'écrit toujours <b>devant</b> la lettre, jamais après, '
            // L'exemple prend une lettre et un nombre ÉTRANGERS à l'exercice (k et 5, hors du pool
            // de lettres) : cité avec ceux de la question, il désignait la proposition à ne pas
            // cocher et donnait la réponse (retour prof 2026-09-24).
            + `car « ${mathEnonce('k5')} » se lirait comme une puissance.</p>`,
            opts(L_VOC), true);
        payload._meta.params = {
            L, type: t.id, niveau: 1, nbVariables: 1, justes,
            distracteurs: pieges.map(p => ({ code: p.code, valeur: p.valeur })),
        };
        return payload;
    }
    // Repli déterministe : le double de x
    const justes = ['2x', '2 × x', 'x × 2'].map(mathChoix);
    const pieges = [
        { code: 'double_et_carre', valeur: mathChoix('x²') },
        { code: 'coefficient_apres_la_lettre', valeur: mathChoix('x2') },
    ];
    const payload = makeQcm([...justes, ...pieges.map(p => p.valeur)], justes,
        `<p><b>${lettreEnonce('x')} est un nombre entier. Coche TOUTES les écritures correctes de `
        + `« le double de ${lettreEnonce('x')} ».</b> Une même formule s'écrit de plusieurs façons.</p>`,
        opts(L_VOC), true);
    payload._meta.params = {
        L: 'x', type: 'double', niveau: 1, nbVariables: 1, justes, distracteurs: pieges,
    };
    return payload;
};

// ═════════ Carte 2 — Relier phrases et expressions (niveau 3) ═════════

const FAMILLES_RELIER = [
    { id: 'somme', phrase: (L, k) => `la somme de ${L} et de ${k}`, expr: (L, k) => `${L} + ${k}` },
    { id: 'prodSomme', phrase: (L, k, a) => `le produit de ${k} par la somme de ${L} et de ${a}`, expr: (L, k, a) => `${k}(${L} + ${a})` },
    { id: 'prodDiff', phrase: (L, k, a) => `le produit de ${k} par la différence entre ${L} et ${a}`, expr: (L, k, a) => `${k}(${L} ${MOINS} ${a})` },
    { id: 'diffProd', phrase: (L, k, a) => `la différence entre le produit de ${k} par ${L} et ${a}`, expr: (L, k, a) => `${k}${L} ${MOINS} ${a}` },
    { id: 'diff', phrase: (L, k) => `la différence entre ${k} et ${L}`, expr: (L, k) => `${k} ${MOINS} ${L}` },
    { id: 'sommeProd', phrase: (L, k, a) => `la somme de ${L} et du produit de ${a} par ${k}`, expr: (L, k, a) => `${L} + ${a} × ${k}` },
    { id: 'prodPlus', phrase: (L, k, a) => `la somme du produit de ${k} par ${L} et de ${a}`, expr: (L, k, a) => `${k}${L} + ${a}` },
];

const genRelier = () => {
    const L = randomChoice(LETTRES);
    const k = randInt(MULT_MIN, MULT_MAX);
    let a = randInt(MULT_MIN, MULT_MAX);
    if (a === k) a = a === MULT_MAX ? MULT_MIN : a + 1;   // k ≠ a par construction
    // toujours une famille parenthésée ET une famille kL ± a (la place des parenthèses)
    const parenthesee = randomChoice([FAMILLES_RELIER[1], FAMILLES_RELIER[2]]);
    const lineaire = randomChoice([FAMILLES_RELIER[3], FAMILLES_RELIER[6]]);
    const autres = shuffle(FAMILLES_RELIER.filter(f => f !== parenthesee && f !== lineaire)).slice(0, 2);
    const pairs = shuffle([parenthesee, lineaire, ...autres]).map(f => ({
        source: f.phrase(lettreTrou(L), k, a),
        target: mathChoix(f.expr(L, k, a)),
    }));
    const payload = makeRelier(pairs,
        `<p><b>${lettreEnonce(L)} est un nombre.</b> Relie chaque phrase à l'expression littérale qui lui correspond.</p>`,
        opts(L_RELIER));
    payload._meta.params = {
        L, k, a, familles: pairs.map(p => p.source), niveau: 3, nbVariables: 1,
        erreurVisee: ['operation_traduite_a_tort'],
    };
    return payload;
};

// ═════════ Carte 3 — Deux nombres, une formule (niveau 3) ═════════

const genDeuxSaisie = () => {
    const [A, B] = deuxLettres();
    const t = randomChoice(TYPES_DEUX);
    const ecritures = t.ecritures(A, B);
    const payload = makeSaisie(ecritures[0],
        `<p><b>${lettreEnonce(A)} et ${lettreEnonce(B)} sont deux nombres. `
        + `Écris ${t.nom(lettreEnonce(A), lettreEnonce(B))} en fonction de ${lettreEnonce(A)} `
        + `et de ${lettreEnonce(B)}.</b> `
        + 'Toutes les écritures qui donnent le même nombre sont acceptées.</p>',
        { ...opts(L_DEUX), comparaison: 'expression', reponses_acceptees: ecritures });
    payload._meta.params = {
        A, B, type: t.id, niveau: 3, nbVariables: 2, ecritures,
        erreurVisee: ['operation_traduite_a_tort'],
    };
    return payload;
};

// ═════════ Carte 4 — Écrire tous les nombres pairs, impairs, multiples (niveau 3) ═════════

const genGeneralQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const L = randomChoice(LETTRES);
        const famille = randomChoice(['pair', 'impair', 'multiple']);
        const k = randInt(3, MULT_MAX);
        const v = randInt(2, 9);
        let question;
        let correct;
        let pieges;
        if (famille === 'pair') {
            question = 'Quelle expression désigne un nombre pair, quel que soit l\'entier';
            correct = `2${L}`;
            pieges = [
                { valeur: String(2 * v), code: 'juste_pour_une_valeur' },
                { valeur: `2${L} + 1`, code: 'operation_traduite_a_tort' },
                { valeur: `${L} + 2`, code: 'operation_traduite_a_tort' },
            ];
        } else if (famille === 'impair') {
            question = 'Quelle expression désigne un nombre impair, quel que soit l\'entier';
            correct = `2${L} + 1`;
            pieges = [
                { valeur: String(2 * v + 1), code: 'juste_pour_une_valeur' },
                { valeur: `2${L}`, code: 'operation_traduite_a_tort' },
                { valeur: `${L} + 1`, code: 'operation_traduite_a_tort' },
            ];
        } else {
            question = `Quelle expression désigne un multiple de ${k}, quel que soit l'entier`;
            correct = `${k}${L}`;
            pieges = [
                { valeur: String(k * v), code: 'juste_pour_une_valeur' },
                { valeur: `${L} + ${k}`, code: 'operation_traduite_a_tort' },
                { valeur: `${L}${k}`, code: 'coefficient_apres_la_lettre' },
            ];
        }
        pieges = pieges.map(p => ({ code: p.code, valeur: mathChoix(p.valeur) }));
        const choix = [mathChoix(correct), ...pieges.map(p => p.valeur)];
        if (new Set(choix).size !== choix.length) continue;
        const payload = makeQcm(choix, mathChoix(correct),
            `<p><b>${question} ${lettreEnonce(L)} ?</b></p>`, opts(L_GENERAL));
        payload._meta.params = {
            L, famille, k, niveau: 3, nbVariables: 1,
            distracteurs: pieges,
        };
        return payload;
    }
    const pieges = [
        { valeur: mathChoix('8'), code: 'juste_pour_une_valeur' },
        { valeur: mathChoix('2n + 1'), code: 'operation_traduite_a_tort' },
        { valeur: mathChoix('n + 2'), code: 'operation_traduite_a_tort' },
    ];
    const payload = makeQcm([mathChoix('2n'), ...pieges.map(p => p.valeur)], mathChoix('2n'),
        `<p><b>Quelle expression désigne un nombre pair, quel que soit l'entier ${lettreEnonce('n')} ?</b></p>`,
        opts(L_GENERAL));
    payload._meta.params = {
        L: 'n', famille: 'pair', k: 2, niveau: 3, nbVariables: 1, distracteurs: pieges,
    };
    return payload;
};

// ═════════ Carte 5 — Traduire un programme de calcul (niveau 4) ═════════

const genProgrammeSaisie = () => {
    const L = randomChoice(LETTRES);
    const p = randInt(COEF_PROG_MIN, COEF_PROG_MAX);
    const q = randInt(COEF_PROG_MIN, COEF_PROG_MAX);
    const ordre = randomChoice(['multiplieDabord', 'ajouteDabord']);
    const instructions = ordre === 'multiplieDabord'
        ? [`Multiplie ce nombre par ${p}.`, `Ajoute ${q} au résultat.`]
        : [`Ajoute ${q} à ce nombre.`, `Multiplie le résultat par ${p}.`];
    const termes = ordre === 'multiplieDabord'
        ? [{ coef: p, deg: 1 }, { coef: q, deg: 0 }]
        : [{ coef: p, deg: 1 }, { coef: p * q, deg: 0 }];
    const ecritures = ecrituresPolynome(termes, L);
    const payload = makeSaisie(ecritures[0],
        `<p><b>Programme de calcul.</b> Choisis un nombre, que l'on appelle ${lettreEnonce(L)}. `
        + `${instructions.join(' ')}</p>`
        + '<p><b>Écris le résultat du programme en fonction de '
        + `${lettreEnonce(L)}, sous forme réduite (${lettreEnonce(L)} apparaît une seule fois).</b></p>`,
        { ...opts(L_PROGRAMME), comparaison: 'expression', reponses_acceptees: ecritures });
    payload._meta.params = {
        L, p, q, ordre, niveau: 4, nbVariables: 1, ecritures,
        erreurVisee: ['operation_traduite_a_tort'],
    };
    return payload;
};

// ═════════ Carte 6 — Aire et périmètre d'un rectangle (niveau 5) ═════════

/**
 * La place à réserver de chaque côté du dessin pour un libellé comme « 12 cm » ou
 * « m cm » : environ 40 px à 15 px de fonte, plus les 12 px qui le décollent du trait.
 * Sans cette marge le libellé de gauche sortait du cadre et se retrouvait coupé
 * (signalé par le prof le 2026-09-24 sur la figure composée).
 */
const MARGE_LIBELLE = 60;

const svgRectangle = (L, h) => {
    const x0 = MARGE_LIBELLE;
    const y0 = 30;
    const w = 190;
    const ht = 90;
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${x0 + w + 20} 160'>`
        + `<rect x='${x0}' y='${y0}' width='${w}' height='${ht}' fill='#E0E7FF' stroke='#1E3A8A' stroke-width='2'/>`
        + `<text x='${x0 + w / 2}' y='${y0 - 10}' text-anchor='middle' font-size='15' fill='#1E3A8A' font-family='sans-serif'>`
        + `<tspan font-style='italic' font-family='Georgia,serif'>${L}</tspan> cm</text>`
        + `<text x='${x0 - 12}' y='${y0 + ht / 2 + 5}' text-anchor='end' font-size='15' fill='#1E3A8A' font-family='sans-serif'>${h} cm</text>`
        + '</svg>';
    return {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: 'data:image/svg+xml,' + encodeURIComponent(svg), maxWidth: 'compact' } }],
    };
};

const tirerRectangle = () => {
    const L = randomChoice(LETTRES);
    const h = randInt(LONGUEUR_MIN, LONGUEUR_MAX);
    const valeurs = shuffle([2, 3, 4, 5, 6, 7, 8, 9, 10]).slice(0, 3).sort((a, b) => a - b);
    return { L, h, valeurs };
};

const TEXTE_RECTANGLE = (L, h) =>
    `<p><b>Un rectangle a une largeur de ${h} cm et une longueur de ${lettreEnonce(L)} cm</b> `
    + `(${lettreEnonce(L)} est un nombre, la figure n'est pas à l'échelle).</p>`;

const genAireTrous = () => {
    const { L, h, valeurs } = tirerRectangle();
    const x = lettreTrou(L);
    const lignes = valeurs.map(v => `Si ${x} = ${v}, l'aire vaut {{${h * v}}} cm²`);
    const phrase = `${lignes.join('\n')}\nPour n'importe quelle valeur, l'aire vaut {{${h}}} × ${x} cm²`;
    const payload = makeTexteTrou(phrase,
        TEXTE_RECTANGLE(L, h) + '<p><b>Complète le tableau de valeurs, puis la formule générale.</b></p>',
        { ...opts(L_AIRE), mode: 'saisie', elements_additionnels: svgRectangle(L, h) });
    payload._meta.params = {
        L, h, valeurs, niveau: 5, nbVariables: 1,
        erreurVisee: ['juste_pour_une_valeur'],
    };
    return payload;
};

const genAireQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const { L, h } = tirerRectangle();
        const correct = mathChoix(`${h}${L}`);
        const pieges = [
            { valeur: mathChoix(`2${L} + ${2 * h}`), code: 'aire_et_perimetre' },
            { valeur: mathChoix(`${L} + ${h}`), code: 'operation_traduite_a_tort' },
            { valeur: mathChoix(`${L}${h}`), code: 'coefficient_apres_la_lettre' },
        ];
        const choix = [correct, ...pieges.map(p => p.valeur)];
        if (new Set(choix).size !== choix.length) continue;
        const payload = makeQcm(choix, correct,
            TEXTE_RECTANGLE(L, h) + '<p><b>Quelle expression donne l\'aire de ce rectangle, en cm² ?</b></p>',
            { ...opts(L_AIRE), elements_additionnels: svgRectangle(L, h) });
        payload._meta.params = { L, h, niveau: 5, nbVariables: 1, distracteurs: pieges };
        return payload;
    }
    const pieges = [
        { valeur: mathChoix('2x + 10'), code: 'aire_et_perimetre' },
        { valeur: mathChoix('x + 5'), code: 'operation_traduite_a_tort' },
        { valeur: mathChoix('x5'), code: 'coefficient_apres_la_lettre' },
    ];
    const payload = makeQcm([mathChoix('5x'), ...pieges.map(p => p.valeur)], mathChoix('5x'),
        TEXTE_RECTANGLE('x', 5) + '<p><b>Quelle expression donne l\'aire de ce rectangle, en cm² ?</b></p>',
        { ...opts(L_AIRE), elements_additionnels: svgRectangle('x', 5) });
    payload._meta.params = { L: 'x', h: 5, niveau: 5, nbVariables: 1, distracteurs: pieges };
    return payload;
};

// ═════════ Carte 7 — Périmètre du polygone (niveau 5) ═════════

// Quadrilatère JILK schématique (pas à l'échelle) : JI (haut) et KL (bas)
// Le nom suit le CONTOUR, J puis I puis L puis K : JIKL passerait par la diagonale
// I-K et ne designerait pas la ligne brisee fermee (retour prof 2026-09-24).
// portent le même codage « ‖ », IL à droite, JK = x à gauche.
const svgPolygone = (a, b) => {
    const J = { x: 78, y: 34 }, I = { x: 218, y: 42 }, Lp = { x: 232, y: 138 }, K = { x: 52, y: 128 };
    const mid = (P, Q) => ({ x: (P.x + Q.x) / 2, y: (P.y + Q.y) / 2 });
    // codage « ‖ » : deux petits traits perpendiculaires au milieu d'un côté
    const codage = (P, Q) => {
        const m = mid(P, Q);
        const dx = Q.x - P.x, dy = Q.y - P.y;
        const len = Math.hypot(dx, dy);
        const nx = -dy / len, ny = dx / len;       // normale unitaire
        const tx = dx / len, ty = dy / len;        // tangente unitaire
        const tick = (off) => {
            const cx = m.x + tx * off, cy = m.y + ty * off;
            return `<line x1='${(cx - nx * 6).toFixed(1)}' y1='${(cy - ny * 6).toFixed(1)}' x2='${(cx + nx * 6).toFixed(1)}' y2='${(cy + ny * 6).toFixed(1)}' stroke='#1E3A8A' stroke-width='2'/>`;
        };
        return tick(-3.5) + tick(3.5);
    };
    const label = (P, Q, txt, off) => {
        const m = mid(P, Q);
        const dx = Q.x - P.x, dy = Q.y - P.y;
        const len = Math.hypot(dx, dy);
        const nx = -dy / len, ny = dx / len;
        return `<text x='${(m.x + nx * off).toFixed(1)}' y='${(m.y + ny * off).toFixed(1)}' text-anchor='middle' font-size='14' fill='#1E3A8A' font-family='sans-serif'>${txt}</text>`;
    };
    const sommet = (P, nom, dxx, dyy) =>
        `<text x='${P.x + dxx}' y='${P.y + dyy}' text-anchor='middle' font-size='14' font-weight='bold' fill='#1E3A8A' font-family='sans-serif'>${nom}</text>`;
    const svg = '<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 290 170\'>'
        + `<polygon points='${J.x},${J.y} ${I.x},${I.y} ${Lp.x},${Lp.y} ${K.x},${K.y}' fill='#E0E7FF' stroke='#1E3A8A' stroke-width='2'/>`
        + codage(J, I) + codage(K, Lp)
        + label(J, I, `${a} cm`, -14) + label(I, Lp, `${b} cm`, -18)
        // La lettre en italique serif, l'unité en romain (même règle que svgRectangle).
        + label(K, Lp, '? cm', 16)
        + label(J, K, '<tspan font-style=\'italic\' font-family=\'Georgia,serif\'>x</tspan> cm', 15)
        + sommet(J, 'J', -8, -6) + sommet(I, 'I', 9, -5)
        + sommet(Lp, 'L', 10, 10) + sommet(K, 'K', -9, 10)
        + '</svg>';
    return {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: 'data:image/svg+xml,' + encodeURIComponent(svg), maxWidth: 'compact' } }],
    };
};

const tirerPolygone = () => {
    const a = randInt(LONGUEUR_MIN, LONGUEUR_MAX);
    let b = randInt(LONGUEUR_MIN, LONGUEUR_MAX);
    if (b === a) b = a === LONGUEUR_MAX ? LONGUEUR_MIN : b + 1;
    return { a, b };
};

const TEXTE_POLY = (a, b) =>
    '<p><b>Sur la figure (pas à l\'échelle), les côtés marqués du même codage ont la même '
    + `longueur.</b> On donne : JI = ${a} cm ; IL = ${b} cm ; JK = ${lettreEnonce('x')} cm.</p>`;

const genPerimetreQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const { a, b } = tirerPolygone();
        const correct = mathChoix(`x + ${2 * a + b}`);
        const pieges = [
            { valeur: mathChoix(`x + ${a + b}`), code: 'operation_traduite_a_tort' },  // oublie le côté codé KL
            { valeur: mathChoix(`${2 * a + b}`), code: 'juste_pour_une_valeur' },      // oublie x
            { valeur: mathChoix(`${a}x`), code: 'aire_et_perimetre' },                 // donne une aire
        ];
        const choix = [correct, ...pieges.map(p => p.valeur)];
        if (new Set(choix).size !== choix.length) continue;
        const payload = makeQcm(choix, correct,
            TEXTE_POLY(a, b) + '<p><b>Quelle expression donne le périmètre du quadrilatère JILK, en cm ?</b></p>',
            { ...opts(L_PERIMETRE), elements_additionnels: svgPolygone(a, b) });
        payload._meta.params = { a, b, niveau: 5, nbVariables: 1, distracteurs: pieges };
        return payload;
    }
    const pieges = [
        { valeur: mathChoix('x + 13'), code: 'operation_traduite_a_tort' },
        { valeur: mathChoix('17'), code: 'juste_pour_une_valeur' },
        { valeur: mathChoix('16x'), code: 'aire_et_perimetre' },
    ];
    const payload = makeQcm([mathChoix('x + 17'), ...pieges.map(p => p.valeur)], mathChoix('x + 17'),
        TEXTE_POLY(4, 9) + '<p><b>Quelle expression donne le périmètre du quadrilatère JILK, en cm ?</b></p>',
        { ...opts(L_PERIMETRE), elements_additionnels: svgPolygone(4, 9) });
    payload._meta.params = { a: 4, b: 9, niveau: 5, nbVariables: 1, distracteurs: pieges };
    return payload;
};

const genPerimetreTrous = () => {
    const { a, b } = tirerPolygone();
    const x = lettreTrou('x');
    // La correction d'un texte à trous est POSITIONNELLE : elle compare le mot du trou 1
    // au mot attendu au trou 1, et ainsi de suite. Trois longueurs alignées dans une
    // somme (« x + {{3}} + {{4}} + {{3}} ») étaient donc un piège : l'élève qui écrivait
    // 4 puis 3 puis 3 avait une somme JUSTE comptée fausse, l'addition étant commutative
    // (retour prof 2026-09-24). Chaque trou porte maintenant le NOM de son côté : aucune
    // permutation n'est plus vraie, « JI = 4 » est simplement faux.
    const phrase = `JI = {{${a}}} cm\nIL = {{${b}}} cm\nKL = {{${a}}} cm\n`
        + `P = ${x} + JI + IL + KL, donc P = ${x} + {{${2 * a + b}}}`;
    const payload = makeTexteTrou(phrase,
        TEXTE_POLY(a, b) + '<p>Complète le calcul du périmètre P (en cm).</p>',
        { ...opts(L_PERIMETRE), mode: 'saisie', elements_additionnels: svgPolygone(a, b) });
    payload._meta.params = {
        a, b, niveau: 5, nbVariables: 1, erreurVisee: ['operation_traduite_a_tort'],
    };
    return payload;
};

// ═════════ Carte 8 — Périmètre d'une figure composée (niveau 7) ═════════

/** Un rectangle (longueur A, hauteur B) et un carré de côté B accolés. */
const svgComposee = (A, B) => {
    const x0 = MARGE_LIBELLE;
    const y0 = 40;
    const w1 = 150;
    const h = 80;
    // La lettre en italique serif, l'unité en romain (même règle que svgRectangle).
    const it = (L) => `<tspan font-style='italic' font-family='Georgia,serif'>${L}</tspan>`;
    // Cette figure porte un libellé des DEUX côtés : la largeur réserve la marge deux fois.
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${x0 + w1 + h + MARGE_LIBELLE} 170'>`
        + `<rect x='${x0}' y='${y0}' width='${w1 + h}' height='${h}' fill='#E0E7FF' stroke='#1E3A8A' stroke-width='2'/>`
        + `<line x1='${x0 + w1}' y1='${y0}' x2='${x0 + w1}' y2='${y0 + h}' stroke='#1E3A8A' stroke-width='1' stroke-dasharray='4 3'/>`
        + `<text x='${x0 + w1 / 2}' y='${y0 - 12}' text-anchor='middle' font-size='15' fill='#1E3A8A' font-family='sans-serif'>${it(A)} cm</text>`
        + `<text x='${x0 + w1 + h / 2}' y='${y0 - 12}' text-anchor='middle' font-size='15' fill='#1E3A8A' font-family='sans-serif'>${it(B)} cm</text>`
        + `<text x='${x0 - 12}' y='${y0 + h / 2 + 5}' text-anchor='end' font-size='15' fill='#1E3A8A' font-family='sans-serif'>${it(B)} cm</text>`
        + `<text x='${x0 + w1 + h + 12}' y='${y0 + h / 2 + 5}' text-anchor='start' font-size='15' fill='#1E3A8A' font-family='sans-serif'>${it(B)} cm</text>`
        + '</svg>';
    return {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: 'data:image/svg+xml,' + encodeURIComponent(svg), maxWidth: 'compact' } }],
    };
};

const genComposeeQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const [A, B] = deuxLettres();
        // Périmètre = A + B (haut) + B (droite) + A + B (bas) + B (gauche) = 2A + 4B
        const correct = mathChoix(`2${A} + 4${B}`);
        const pieges = [
            { valeur: mathChoix(`2${A} + 2${B}`), code: 'operation_traduite_a_tort' },  // lue comme un simple rectangle
            { valeur: mathChoix(`${A}${B} + ${B}²`), code: 'aire_et_perimetre' },       // c'est l'aire
            { valeur: mathChoix(`${A} + 3${B}`), code: 'operation_traduite_a_tort' },   // un côté oublié
        ];
        const choix = [correct, ...pieges.map(p => p.valeur)];
        if (new Set(choix).size !== choix.length) continue;
        const payload = makeQcm(choix, correct,
            `<p><b>La figure est faite d'un rectangle et d'un carré accolés (pas à l'échelle).</b> `
            + `Les longueurs ${lettreEnonce(A)} et ${lettreEnonce(B)} sont des nombres, en centimètres.</p>`
            + '<p><b>Quelle expression donne le périmètre de la figure entière, en cm ?</b></p>',
            { ...opts(L_COMPOSEE), elements_additionnels: svgComposee(A, B) });
        payload._meta.params = {
            A, B, niveau: 7, nbVariables: 2, nbFigures: 2, distracteurs: pieges,
        };
        return payload;
    }
    const pieges = [
        { valeur: mathChoix('2x + 2y'), code: 'operation_traduite_a_tort' },
        { valeur: mathChoix('xy + y²'), code: 'aire_et_perimetre' },
        { valeur: mathChoix('x + 3y'), code: 'operation_traduite_a_tort' },
    ];
    const payload = makeQcm([mathChoix('2x + 4y'), ...pieges.map(p => p.valeur)], mathChoix('2x + 4y'),
        '<p><b>La figure est faite d\'un rectangle et d\'un carré accolés (pas à l\'échelle).</b> '
        + `Les longueurs ${lettreEnonce('x')} et ${lettreEnonce('y')} sont des nombres, en centimètres.</p>`
        + '<p><b>Quelle expression donne le périmètre de la figure entière, en cm ?</b></p>',
        { ...opts(L_COMPOSEE), elements_additionnels: svgComposee('x', 'y') });
    payload._meta.params = {
        A: 'x', B: 'y', niveau: 7, nbVariables: 2, nbFigures: 2, distracteurs: pieges,
    };
    return payload;
};

// ═════════ Carte 9 — Suite de motifs (niveau 6) ═════════

// familles kn + c : le motif de l'étape n est dessiné en k rangées de n carrés,
// la rangée du bas raccourcie (c < 0) ou allongée (c > 0) de |c| carrés →
// le comptage vaut kn + c PAR CONSTRUCTION. Aucun quotient (exclusions 2).
const FAMILLES_MOTIF = [
    { k: 2, c: -1, formule: `2n ${MOINS} 1` },
    { k: 2, c: 1, formule: '2n + 1' },
    { k: 2, c: 2, formule: '2n + 2' },
    { k: 3, c: 0, formule: '3n' },
    { k: 1, c: 3, formule: 'n + 3' },
];

const compteMotif = (fam, n) => fam.k * n + fam.c;
// variables.nombre_de_motifs_montres : « Trois par défaut, quatre quand la
// suite est affine » (c ≠ 0).
const motifsMontres = (fam) => (fam.c === 0 ? 3 : 4);

const svgMotifs = (fam, nbMotifs) => {
    const s = 13;                     // côté d'un carré (px)
    const parts = [];
    let y = 18;
    for (let etape = 1; etape <= nbMotifs; etape++) {
        const yEtape = y;   // figé : la fermeture ci-dessous ne doit pas lire le y qui avance
        parts.push(`<text x='4' y='${yEtape + s}' font-size='12' fill='#1E3A8A' font-family='sans-serif'>étape ${etape}</text>`);
        const x0 = 62;
        // rangées pleines (k − 1 rangées de n), puis rangée du bas de n + c carrés
        const rangees = [];
        for (let r = 0; r < fam.k - 1; r++) rangees.push(etape);
        rangees.push(etape + fam.c);
        rangees.forEach((nb, r) => {
            for (let i = 0; i < nb; i++) {
                parts.push(`<rect x='${x0 + i * (s + 2)}' y='${yEtape + r * (s + 2)}' width='${s}' height='${s}' fill='#C7D2FE' stroke='#1E3A8A' stroke-width='1.5'/>`);
            }
        });
        y += rangees.length * (s + 2) + 12;
    }
    const largeur = 62 + (nbMotifs + Math.max(fam.c, 0)) * (s + 2) + 10;
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${largeur} ${y}'>${parts.join('')}</svg>`;
    return {
        version: 1,
        elements: [{ type: 'image', config: { imageUrl: 'data:image/svg+xml,' + encodeURIComponent(svg), maxWidth: 'compact' } }],
    };
};

const genMotifsTrous = () => {
    const fam = randomChoice(FAMILLES_MOTIF);
    const nbMotifs = motifsMontres(fam);
    // exclusions 8 : le rang demandé dépasse toujours le nombre de motifs montrés.
    const N = randInt(RANG_MIN, RANG_MAX);
    // Une étape par ligne (convention 2026-09-02 : jamais « ; » entre deux
    // trous indépendants, l'impression coupait la ligne au milieu).
    const lignes = ['Nombre de carrés :'];
    for (let e = 1; e <= nbMotifs; e++) lignes.push(`Étape ${e} → {{${compteMotif(fam, e)}}}`);
    lignes.push(`Étape ${N} → {{${compteMotif(fam, N)}}}`);
    const payload = makeTexteTrou(lignes.join('\n'),
        `<p><b>On construit une suite de motifs avec des carrés (étapes 1 à ${nbMotifs} ci-dessous).</b> `
        + 'Compte les carrés de chaque étape, puis poursuis la régularité.</p>',
        { ...opts(L_MOTIFS), mode: 'saisie', elements_additionnels: svgMotifs(fam, nbMotifs) });
    payload._meta.params = {
        fam, N, nbMotifs, niveau: 6, nbVariables: 1,
        erreurVisee: ['proportionnalite_supposee'],
    };
    return payload;
};

const genMotifsQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const fam = randomChoice(FAMILLES_MOTIF);
        const nbMotifs = motifsMontres(fam);
        const correct = fam.formule;
        // Deux affines distinctes diffèrent sur au moins une des étapes montrées :
        // aucune proposition fausse n'est confondable avec la bonne.
        const pieges = [
            ...(fam.c !== 0 ? [{ valeur: fam.k === 1 ? 'n' : `${fam.k}n`, code: 'proportionnalite_supposee' }] : []),
            ...FAMILLES_MOTIF.filter(f => f !== fam).map(f => ({ valeur: f.formule, code: 'proportionnalite_supposee' })),
            { valeur: 'n²', code: 'double_et_carre' },
        ];
        const retenus = [];
        const vus = new Set([correct]);
        shuffle(pieges).forEach(p => {
            if (vus.has(p.valeur)) return;
            vus.add(p.valeur);
            retenus.push(p);
        });
        if (retenus.length < 3) continue;
        const distracteurs = fam.c !== 0
            ? [pieges[0], ...retenus.filter(p => p.valeur !== pieges[0].valeur).slice(0, 2)]
            : retenus.slice(0, 3);
        // Les valeurs des distracteurs restent BRUTES dans _meta : elles y sont relues
        // formule par formule (le contrôle « une seule formule colle aux motifs »).
        const choix = [mathChoix(correct), ...distracteurs.map(p => mathChoix(p.valeur))];
        if (new Set(choix).size !== choix.length) continue;
        const payload = makeQcm(choix, mathChoix(correct),
            `<p><b>On construit une suite de motifs avec des carrés (étapes 1 à ${nbMotifs} ci-dessous).</b> `
            + `Quelle formule donne le nombre de carrés à l'étape ${lettreEnonce('n')} ?</p>`,
            { ...opts(L_MOTIFS), elements_additionnels: svgMotifs(fam, nbMotifs) });
        payload._meta.params = { fam, nbMotifs, niveau: 6, nbVariables: 1, distracteurs };
        return payload;
    }
    const fam = FAMILLES_MOTIF[0];
    const distracteurs = [
        { valeur: '2n', code: 'proportionnalite_supposee' },
        { valeur: '3n', code: 'proportionnalite_supposee' },
        { valeur: 'n²', code: 'double_et_carre' },
    ];
    const payload = makeQcm([mathChoix(fam.formule), ...distracteurs.map(p => mathChoix(p.valeur))],
        mathChoix(fam.formule),
        '<p><b>On construit une suite de motifs avec des carrés (étapes 1 à 4 ci-dessous).</b> '
        + `Quelle formule donne le nombre de carrés à l'étape ${lettreEnonce('n')} ?</p>`,
        { ...opts(L_MOTIFS), elements_additionnels: svgMotifs(fam, 4) });
    payload._meta.params = { fam, nbMotifs: 4, niveau: 6, nbVariables: 1, distracteurs };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    voc_texte_trou_drag: genVocDrag,
    voc_qcm_unique: () => sansRepeter(construireVocQcm, refVocQcm),
    voc_texte: () => sansRepeter(construireVocSaisie, refVocSaisie),
    voc_qcm_multiple: genVocQcmMultiple,
    relier_relier: genRelier,
    deux_texte: genDeuxSaisie,
    general_qcm_unique: genGeneralQcm,
    programme_texte: genProgrammeSaisie,
    aire_texte_trou_saisie: genAireTrous,
    aire_qcm_unique: genAireQcm,
    perimetre_qcm_unique: genPerimetreQcm,
    perimetre_texte_trou_saisie: genPerimetreTrous,
    composee_qcm_unique: genComposeeQcm,
    motifs_texte_trou_saisie: genMotifsTrous,
    motifs_qcm_unique: genMotifsQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM à cocher', relier: 'Relier',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous', texte: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'voc', label: L_VOC, formatsList: buildFormats('voc') },
    { id: 'relier', label: L_RELIER, formatsList: buildFormats('relier') },
    { id: 'deux', label: L_DEUX, formatsList: buildFormats('deux') },
    { id: 'general', label: L_GENERAL, formatsList: buildFormats('general') },
    { id: 'programme', label: L_PROGRAMME, formatsList: buildFormats('programme') },
    { id: 'aire', label: L_AIRE, formatsList: buildFormats('aire') },
    { id: 'perimetre', label: L_PERIMETRE, formatsList: buildFormats('perimetre') },
    { id: 'composee', label: L_COMPOSEE, formatsList: buildFormats('composee') },
    { id: 'motifs', label: L_MOTIFS, formatsList: buildFormats('motifs') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
